import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useScrollAnimation from "../../hooks/useScrollAnimation";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";

type ProjectCategory =
  | "Tüm Projeler"
  | "Tabela"
  | "Dekorasyon"
  | "LED"
  | "Baskı";

type ProjectItem = {
  title: string;
  category: Exclude<ProjectCategory, "Tüm Projeler">;
  location: string;
  year: string;
  img: string;
  serviceSlug: string;
};

const CATEGORIES: ProjectCategory[] = [
  "Tüm Projeler",
  "Tabela",
  "Dekorasyon",
  "LED",
  "Baskı",
];

const PROJECTS: ProjectItem[] = [
  {
    title: "Kurumsal Totem Uygulaması",
    category: "Tabela",
    location: "Osmaniye",
    year: "2025",
    img: "/images/totemt/1.webp",
    serviceSlug: "totem-tabela",
  },
  {
    title: "Paslanmaz Kutu Harf Uygulaması",
    category: "Tabela",
    location: "Osmaniye",
    year: "2025",
    img: "/images/kharft/6-1.webp",
    serviceSlug: "kutu-harf-tabela",
  },
  {
    title: "Cafe Konsept Uygulaması",
    category: "Dekorasyon",
    location: "Osmaniye",
    year: "2025",
    img: "/images/cafe/c (3).webp",
    serviceSlug: "cafe-restorant-dekorasyon",
  },
  {
    title: "Restorant İç Mekan Yenileme",
    category: "Dekorasyon",
    location: "Osmaniye",
    year: "2024",
    img: "/images/cafe/c (1).webp",
    serviceSlug: "cafe-restorant-dekorasyon",
  },
  {
    title: "LED Cephe Uygulaması",
    category: "LED",
    location: "Osmaniye",
    year: "2025",
    img: "/images/led/3.webp",
    serviceSlug: "led-teknolojisi",
  },
  {
    title: "RGB Piksel LED Sistem",
    category: "LED",
    location: "Osmaniye",
    year: "2024",
    img: "/images/led/l-2.webp",
    serviceSlug: "led-teknolojisi",
  },
  {
    title: "Geniş Format Branda Baskı",
    category: "Baskı",
    location: "Osmaniye",
    year: "2025",
    img: "/images/mat/m-1.webp",
    serviceSlug: "matbaa-baski",
  },
  {
    title: "Özel Kesim Folyo Uygulaması",
    category: "Baskı",
    location: "Osmaniye",
    year: "2024",
    img: "/images/mat/m-4.webp",
    serviceSlug: "matbaa-baski",
  },
  {
    title: "Anahtar Teslim Mağaza Dönüşümü",
    category: "Tabela",
    location: "Osmaniye",
    year: "2024",
    img: "/images/a/a.webp",
    serviceSlug: "anahtar-teslim-proje",
  },
];

const ProjelerPage = () => {
  useScrollAnimation();
  const [activeCategory, setActiveCategory] = useState<ProjectCategory>("Tüm Projeler");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredProjects = useMemo(() => {
    if (activeCategory === "Tüm Projeler") return PROJECTS;
    return PROJECTS.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative pt-32 pb-20 bg-[#0D0D0D] overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="/images/menu-icin-cephe-44a1019c.png"
            alt="Projeler"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute top-0 left-0 w-1.5 h-full bg-[#E6A800]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 mb-6 anim-down d0">
            <Link to="/" className="text-white/40 text-xs font-head hover:text-[#E6A800] tr uppercase tracking-wider">
              Ana Sayfa
            </Link>
            <i className="ri-arrow-right-s-line text-white/30 text-xs" />
            <span className="text-[#E6A800] text-xs font-head uppercase tracking-wider">Projeler</span>
          </div>
          <div className="flex items-center gap-3 mb-5 anim-left d1">
            <div className="w-8 h-[2px] bg-[#E6A800]" />
            <span className="font-head text-[#E6A800] text-xs font-700 uppercase tracking-[0.2em]">Portföy</span>
          </div>
          <h1 className="font-head font-black uppercase text-white text-[clamp(48px,8vw,100px)] leading-[0.88] anim-up d2">
            Tamamlanan
            <br />
            <span className="text-[#E6A800]">Projelerimiz</span>
          </h1>
          <p className="text-white/50 text-sm leading-relaxed max-w-lg mt-6 anim-up d3">
            Tabela, LED, baskı ve dekorasyon alanlarında tamamladığımız projeleri
            kategorilere göre inceleyin. Tüm uygulamalar keşif, üretim ve montaj
            sürecinin tek ekip tarafından yönetildiği gerçek saha çalışmalarıdır.
          </p>
        </div>
      </section>

      <section className="py-10 bg-[#F7F7F7] border-y border-[#ECECEC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-3 anim-up">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 font-head font-700 uppercase text-xs tracking-wider tr border ${
                  activeCategory === category
                    ? "bg-[#E6A800] border-[#E6A800] text-white"
                    : "bg-white border-[#E2E2E2] text-[#666] hover:border-[#E6A800] hover:text-[#E6A800]"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, i) => (
              <Link
                key={`${project.title}-${project.img}`}
                to={`/hizmetler/${project.serviceSlug}`}
                className="group overflow-hidden border border-[#E8E8E8] hover:border-[#E6A800] tr anim-up"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div className="relative h-[240px] overflow-hidden">
                  <img
                    src={project.img}
                    alt={project.title}
                    className="w-full h-full object-cover object-center tr group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/75 via-[#0D0D0D]/15 to-transparent" />
                  <span className="absolute top-4 left-4 px-3 py-1 bg-white/15 border border-white/20 text-white text-[10px] font-head font-700 uppercase tracking-wider">
                    {project.category}
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="text-white/50 text-[11px] font-head uppercase tracking-wider mb-1">
                      {project.location} · {project.year}
                    </p>
                    <h3 className="font-head font-black uppercase text-white text-xl leading-tight">
                      {project.title}
                    </h3>
                  </div>
                  <div className="absolute bottom-0 left-0 h-[3px] bg-[#E6A800] w-0 group-hover:w-full tr" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#181614]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center anim-scale">
          <div className="w-10 h-[3px] bg-[#E6A800] mx-auto mb-5" />
          <h2 className="font-head font-black uppercase text-white text-4xl md:text-5xl leading-[0.9] mb-5">
            Projeniz İçin
            <br />
            <span className="text-[#E6A800]">Hemen İletişime Geçin</span>
          </h2>
          <p className="text-white/60 text-sm leading-relaxed max-w-2xl mx-auto mb-8">
            Markanıza uygun uygulama planını oluşturmak için ücretsiz keşif ve
            teklif desteği veriyoruz.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/hizmetler"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#E6A800] text-white hover:bg-[#C99500] tr font-head font-700 uppercase text-xs tracking-wider"
            >
              Hizmetleri İncele
              <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="tel:+905511830918"
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/25 text-white hover:bg-white/10 tr font-head font-700 uppercase text-xs tracking-wider"
            >
              <i className="ri-phone-line" />
              +90 551 183 09 18
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjelerPage;
